import { Game, GameSession } from "@onmo/graphql/graphql";
import DefaultGame from "../assets/icons/default_game_1x.webp";
import DefaultGame2x from "../assets/icons/default_game_2x.webp";
import DefaultGame3x from "../assets/icons/default_game_3x.webp";
import { getImageWithFallback } from "./game";
import { MAX_WIDTH_TABLET } from "../constants/dimension";
import { PageRoutes } from "../constants/routes";
import { SETTINGS_VIEW_TITLE_KEYS, SettingsView } from "../constants/settings";
import { getRoutePath } from "./location";

const isGameSession = (v?: Game | GameSession): v is GameSession =>
  v != null && typeof v === "object" && "game" in v;

const parseMetadata = (meta: unknown): any => {
  if (!meta) return {};
  if (typeof meta === "string") {
    try {
      return JSON.parse(meta);
    } catch {
      return {};
    }
  }
  return typeof meta === "object" ? meta : {};
};

const getContent = (input?: GameSession | Game) => {
  const game: Game | null = isGameSession(input)
    ? input.game ?? null
    : input ?? null;
  const meta = parseMetadata(game?.metadata);

  return {
    src1x:
      getImageWithFallback(meta?.gameImages, "small", "small_aaa") ??
      DefaultGame,
    src2x:
      getImageWithFallback(meta?.gameImages, "small2x", "small_aaa2x") ??
      DefaultGame2x,
    src3x:
      getImageWithFallback(meta?.gameImages, "small3x", "small_aaa3x") ??
      DefaultGame3x,
  };
};

const hideNav = (pathname: string) => {
  return (
    window.innerWidth <= MAX_WIDTH_TABLET && pathname === PageRoutes.Settings
  );
};

export const getPageByPath = (pathname?: string) => {
  const path = pathname || getRoutePath();
  if (path.startsWith(PageRoutes.EssentialPass))
    return "Navigation_Essential Pass";
  if (path.startsWith(PageRoutes.InstallGame))
    return "Navigation_Install & Play";
  if (path.startsWith(PageRoutes.Settings)) return "Navigation_Settings";
  if (path.startsWith(PageRoutes.Game)) return "Navigation_Game Details";
  if (path.startsWith(PageRoutes.Search)) return "Navigation_Search";
  return "";
};

const PAGE_TITLE_KEYS: Record<string, string> = {
  [PageRoutes.Home]: "Navigation_Console",
  [PageRoutes.MyGames]: "Navigation_My Games",
  [PageRoutes.General]: SETTINGS_VIEW_TITLE_KEYS[SettingsView.General],
  [PageRoutes.ProfileInfo]: SETTINGS_VIEW_TITLE_KEYS[SettingsView.ProfileInfo],
  [PageRoutes.Subscription]:
    SETTINGS_VIEW_TITLE_KEYS[SettingsView.Subscription],
  [PageRoutes.StreamingProfile]:
    SETTINGS_VIEW_TITLE_KEYS[SettingsView.StreamingProfile],
  [PageRoutes.CookieSettings]:
    SETTINGS_VIEW_TITLE_KEYS[SettingsView.CookieSettings],
  [PageRoutes.Exit]: SETTINGS_VIEW_TITLE_KEYS[SettingsView.Exit],
  [PageRoutes.LegalInformation]:
    SETTINGS_VIEW_TITLE_KEYS[SettingsView.LegalInformation],
  [PageRoutes.Privacy]: SETTINGS_VIEW_TITLE_KEYS[SettingsView.Privacy],
  [PageRoutes.Tnc]: SETTINGS_VIEW_TITLE_KEYS[SettingsView.Tnc],
  [PageRoutes.CookiePolicy]:
    SETTINGS_VIEW_TITLE_KEYS[SettingsView.CookiePolicy],
  [PageRoutes.Support]: SETTINGS_VIEW_TITLE_KEYS[SettingsView.Support],
  [PageRoutes.Activate]: "Activation_Activate",
  [PageRoutes.Paywall]: "A11y_Page Subscribe",
  [PageRoutes.Payment]: "Payment_Payment",
  [PageRoutes.Onboarding]: "A11y_Page Welcome",
  [PageRoutes.WhoIsPlaying]: "WhoIsPlaying_Who's playing?",
  [PageRoutes.LoginQR]: "Onboarding_Login",
  [PageRoutes.ErrorNetwork]: "ErrorNetwork_Oops, Something Went Wrong",
};

// document.title only; getPageByPath also drives the visible mobile header
export const getPageTitleKey = (rawPathname: string) => {
  const pathname =
    rawPathname.length > 1 ? rawPathname.replace(/\/+$/, "") : rawPathname;
  if (PAGE_TITLE_KEYS[pathname]) return PAGE_TITLE_KEYS[pathname];
  if (pathname.startsWith(PageRoutes.OpenSource))
    return SETTINGS_VIEW_TITLE_KEYS[SettingsView.OpenSource];
  if (pathname.startsWith(PageRoutes.GamePlay)) return "A11y_Page Playing";
  return getPageByPath(pathname);
};

export const NavigationMd = {
  getContent,
  isGameSession,
  hideNav,
};
